import { v4 as uuidv4 } from 'uuid';
import * as THREE from 'three';
import { BaseTool } from '../BaseTool';
import { ThreeEvent } from '@react-three/fiber';
import { View, Element } from '../../core/types';

export interface SectionElement extends Element {
    category: 'Views';
    geometryData: {
        start: [number, number, number];
        end: [number, number, number];
        viewId: string;
        depth: number;
        /** Look direction (perpendicular to the cut line, plan XY). */
        normal?: [number, number, number];
    };
}

export class SectionTool extends BaseTool {
    id = 'VIEW_SECTION';
    name = 'Section';
    cursor = 'crosshair';

    private startPoint: THREE.Vector3 | null = null;
    private currentPoint: THREE.Vector3 | null = null;

    onPointerDown(event: ThreeEvent<PointerEvent>): void {
        if (!this.context) return;
        if (event.button !== 0) return;

        const point = this.getIntersection(event);
        if (!point) return;
        const snapped = this.snapToGrid(point);

        if (!this.startPoint) {
            this.startPoint = snapped;
            this.currentPoint = snapped;
            // Preview
            this.updatePreview();
        } else {
            // Finish Section
            this.createSection(this.startPoint, snapped);
            this.startPoint = null;
            this.currentPoint = null;
            this.context.appStore.setToolPreview(null);
            this.context.appStore.setActiveTool('select');
        }
    }

    onPointerMove(event: ThreeEvent<PointerEvent>): void {
        if (!this.context || !this.startPoint) return;
        const point = this.getIntersection(event);
        if (point) {
            this.currentPoint = this.snapToGrid(point);
            this.updatePreview();
        }
    }

    private updatePreview() {
        if (!this.context || !this.startPoint || !this.currentPoint) return;
        this.context.appStore.setToolPreview({
            type: 'line',
            start: this.startPoint,
            end: this.currentPoint,
            color: '#0000FF'
        });
    }

    private createSection(start: THREE.Vector3, end: THREE.Vector3) {
        if (!this.context) return;

        const viewId = uuidv4();
        const elementId = uuidv4();
        const name = `Section ${this.context.bimStore.project.views.size + 1}`;

        // Create View
        const newView: View = {
            id: viewId,
            name: name,
            type: 'Section',
            discipline: 'Architectural',
            scale: 100,
            detailLevel: 'Fine',
            visibility: {},
            filters: [],
            // Calculate cut plane
            cutPlane: {
                origin: [start.x, start.y, start.z], // Simplified
                normal: [0, 0, 0] // Needs calculation based on line direction
                // For a vertical section on XY plane:
                // Direction = end - start
                // Normal = Direction x Z (Cross product)
            },
            camera: {
                position: [start.x, start.y, start.z + 1.5],
                target: [end.x, end.y, end.z + 1.5],
                projection: 'Orthographic'
            }
        };

        // Calculate Normal
        const dir = new THREE.Vector3().subVectors(end, start).normalize();
        const normal = new THREE.Vector3(0, 0, 1).cross(dir).normalize();
        if (newView.cutPlane) {
            newView.cutPlane.normal = [normal.x, normal.y, normal.z];
        }

        // Orthographic side camera: stand back from the section midpoint along
        // the -normal direction so the model sits behind the cut plane.
        const mid = start.clone().add(end).multiplyScalar(0.5);
        newView.camera = {
            position: [mid.x - normal.x * 30, mid.y - normal.y * 30, mid.z + 3],
            target: [mid.x, mid.y, mid.z + 3],
            projection: 'Orthographic'
        };

        // Create Section Marker Element
        const sectionEl: SectionElement = {
            id: elementId,
            category: 'Views',
            type: 'Section Marker',
            parameters: {
                'View Name': name,
                'View Id': viewId
            },
            constraints: [],
            geometryData: {
                start: [start.x, start.y, start.z],
                end: [end.x, end.y, end.z],
                viewId: viewId,
                depth: 10, // Default view depth
                // Direction the section looks (perpendicular to the cut line)
                normal: [normal.x, normal.y, 0]
            }
        };

        this.context.bimStore.addView(newView);
        this.context.bimStore.addElement(sectionEl);
        console.log(`Created Section View: ${name}`);
    }

    onPointerUp(): void { }
}
